/**
 * Recursive tree renderer.
 *
 * Each directory node owns a TanStack Query that fetches its immediate
 * children on demand. This keeps the data flow shallow — there's no
 * giant pre-fetched JSON for the entire scope, and the user only pays
 * for what they expand.
 */
import type { FsTreeNode } from '@chaos/types/fs';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import { fsApi, fsKeys } from '../../lib/api/fs';
import { useFileExplorerStore } from '../../stores/file-explorer-store';
import { type ContextMenuPosition, FileTreeContextMenu, FileTreeNode } from './file-tree-node';

interface FileTreeProps {
  rootPath: string;
  onOpenFile: (node: FsTreeNode) => void;
}

export function FileTree({ rootPath, onOpenFile }: FileTreeProps) {
  const rootQ = useQuery({
    queryKey: fsKeys.list(rootPath),
    queryFn: () => fsApi.list({ path: rootPath }),
    staleTime: 30_000,
  });

  const [menu, setMenu] = React.useState<ContextMenuPosition | null>(null);

  const handleContextMenu = React.useCallback((node: FsTreeNode, e: React.MouseEvent) => {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, path: node.path, isDir: node.kind === 'dir' });
  }, []);

  if (rootQ.isLoading) {
    return <div className="px-3 py-2 text-[12px] text-muted-foreground">Loading tree…</div>;
  }
  if (rootQ.error || !rootQ.data) {
    const message =
      rootQ.error instanceof Error ? rootQ.error.message : 'Could not load this scope.';
    return <div className="px-3 py-2 text-[12px] text-error">{message}</div>;
  }

  return (
    <>
      <ul className="flex flex-col">
        {(rootQ.data.children ?? []).map((child) => (
          <TreeNodeRow
            key={child.path}
            node={child}
            depth={0}
            onOpenFile={onOpenFile}
            onContextMenu={handleContextMenu}
          />
        ))}
        {(rootQ.data.children ?? []).length === 0 && (
          <li className="px-3 py-2 text-[12px] text-muted-foreground">Empty directory.</li>
        )}
      </ul>
      {menu && (
        <FileTreeContextMenu
          pos={menu}
          isPinned={false}
          onCopy={() => {
            void navigator.clipboard.writeText(menu.path).catch(() => undefined);
            setMenu(null);
          }}
          onPin={() => setMenu(null)}
          onUnpin={() => setMenu(null)}
          onClose={() => setMenu(null)}
        />
      )}
    </>
  );
}

interface TreeNodeRowProps {
  node: FsTreeNode;
  depth: number;
  onOpenFile: (node: FsTreeNode) => void;
  onContextMenu: (node: FsTreeNode, e: React.MouseEvent) => void;
}

function TreeNodeRow({ node, depth, onOpenFile, onContextMenu }: TreeNodeRowProps) {
  const expanded = useFileExplorerStore((s) => s.expanded[node.path] === true);
  const selectedPath = useFileExplorerStore((s) => s.selectedPath);
  const toggleExpanded = useFileExplorerStore((s) => s.toggleExpanded);

  const isDir = node.kind === 'dir';
  const isSelected = selectedPath === node.path;

  // Only fetch children once the directory is expanded.
  const childrenQ = useQuery({
    queryKey: fsKeys.list(node.path),
    queryFn: () => fsApi.list({ path: node.path }),
    enabled: isDir && expanded,
    staleTime: 30_000,
  });

  const toggle = React.useCallback(() => {
    toggleExpanded(node.path);
  }, [node.path, toggleExpanded]);

  const select = React.useCallback(() => {
    onOpenFile(node);
  }, [node, onOpenFile]);

  const childrenContent =
    isDir && expanded ? (
      <>
        {(childrenQ.data?.children ?? []).map((child) => (
          <TreeNodeRow
            key={child.path}
            node={child}
            depth={depth + 1}
            onOpenFile={onOpenFile}
            onContextMenu={onContextMenu}
          />
        ))}
        {childrenQ.data && (childrenQ.data.children ?? []).length === 0 && !childrenQ.isLoading && (
          <li
            className="text-[11px] text-muted-foreground/70"
            style={{ paddingLeft: `${4 + (depth + 1) * 12 + 12}px` }}
          >
            empty
          </li>
        )}
        {childrenQ.error && (
          <li
            className="text-[11px] text-error"
            style={{ paddingLeft: `${4 + (depth + 1) * 12 + 12}px` }}
          >
            {childrenQ.error instanceof Error ? childrenQ.error.message : 'load failed'}
          </li>
        )}
      </>
    ) : null;

  return (
    <FileTreeNode
      node={node}
      depth={depth}
      isExpanded={expanded}
      isSelected={isSelected}
      isLoading={isDir && expanded && childrenQ.isLoading}
      onToggle={toggle}
      onSelect={select}
      onContextMenu={(e) => onContextMenu(node, e)}
      childrenContent={childrenContent}
    />
  );
}
